import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Availability from './pages/Availability';
import Games from './pages/Games';
import Leagues from './pages/Leagues';
import BookingLinks from './pages/BookingLinks';
import Settings from './pages/Settings';
import PublicBook from './pages/PublicBook';
import PublicProfile from './pages/PublicProfile';
import MyProfile from './pages/MyProfile';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import Friends from './pages/Friends';
import FriendBook from './pages/FriendBook';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SplashScreen from './components/SplashScreen';

// Home page component - shows Landing for guests, Profile for logged in users
function HomePage() {
  const { isAuthenticated, user } = useAuth();
  if (isAuthenticated && user?.profileSlug) {
    return <Navigate to={`/p/${user.profileSlug}`} replace />;
  }
  if (isAuthenticated) {
    // User logged in but no profile slug set yet - go to settings to set one
    return <Navigate to="/my-profile" replace />;
  }
  return <Landing />;
}

function App() {
  const { isLoading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [appReady, setAppReady] = useState(false);

  useEffect(() => {
    // App is ready when auth check is complete
    if (!isLoading) {
      setAppReady(true);
    }
  }, [isLoading]);

  // Show splash screen on initial load
  if (showSplash) {
    return (
      <SplashScreen
        minDisplayTime={2000}
        onComplete={() => setShowSplash(false)}
      />
    );
  }

  // Wait for auth to be ready after splash
  if (!appReady) {
    return (
      <SplashScreen
        minDisplayTime={500}
        onComplete={() => {}}
      />
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/resend-verification" element={<ResendVerification />} />
      <Route path="/book/:token" element={<PublicBook />} />
      <Route path="/p/:slug" element={<PublicProfile />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/games" element={<Games />} />
          <Route path="/leagues" element={<Leagues />} />
          <Route path="/links" element={<BookingLinks />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/my-profile" element={<MyProfile />} />
          <Route path="/friends" element={<Friends />} />
          <Route path="/friends/:friendId/book" element={<FriendBook />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
